import { keyLines, shortened } from "../../common/age-identity";
import { fromBase64 } from "./encoding";

// age is the one family here that is a file format rather than a cipher, and the format's own author publishes the
// TypeScript that reads it — so this page runs typage rather than a second reading of the specification, which is the
// same argument `box.ts` makes the other way round: there, TweetNaCl would have been a second copy of Salsa.
export interface AgeSettings {
  // A file is sealed to one or more recipients, or to a passphrase — which is scrypt, and the only stanza there.
  recipient: "key" | "password";
  recipients: string;
  identities: string;
  password: string;
  // `age -a`, which is how the sealed file is written down rather than anything about how it was sealed.
  armor: boolean;
}

export async function ageEncrypt(data: Uint8Array, settings: AgeSettings): Promise<Uint8Array> {
  const { Encrypter } = await library();
  const encrypter = new Encrypter();
  if (settings.recipient === "password") {
    encrypter.setPassphrase(settings.password);
  } else {
    const recipients = keyLines(settings.recipients);
    if (recipients.length === 0) throw new Error("That is nobody to seal a file to");
    for (const recipient of recipients) readKey(() => encrypter.addRecipient(recipient), "recipient", recipient);
  }
  return encrypter.encrypt(data);
}

export async function ageDecrypt(payload: Uint8Array, settings: AgeSettings): Promise<Uint8Array> {
  const { Decrypter } = await library();
  const decrypter = new Decrypter();
  if (settings.recipient === "password") {
    decrypter.addPassphrase(settings.password);
  } else {
    const identities = keyLines(settings.identities);
    if (identities.length === 0) throw new Error("That is nobody to open a file as");
    for (const identity of identities) readKey(() => decrypter.addIdentity(identity), "identity", identity);
  }

  try {
    return await decrypter.decrypt(payload);
  } catch (e) {
    // typage answers a wrong passphrase and a key that is somebody else's with the one sentence, since to it they are
    // the same thing; which of the two was being offered is this page's to know and so this page's to say.
    if (e instanceof Error && e.message === UNOPENED) {
      throw new Error(
        settings.recipient === "password"
          ? "That passphrase does not open this file"
          : "None of those identities opens this file",
      );
    }
    throw e;
  }
}

// What `age -a` writes and what somebody pastes into a message: the same file, Base64 in a PEM envelope of its own.
export async function ageArmor(payload: Uint8Array): Promise<string> {
  return (await library()).armor.encode(payload);
}

// `-a` is a choice on the way out and never a question on the way back: age reads a raw file and an armoured one
// without being told which it was handed, and a file opening with the PEM line is armoured whatever the box on this
// page last said. So nothing here is a decrypt setting, and the dropzone takes either without a control of its own.
export async function ageFile(file: Uint8Array): Promise<Uint8Array> {
  const head = ASCII.decode(file.subarray(0, ARMOR_BEGIN.length));
  return head === ARMOR_BEGIN ? ageUnarmor(ASCII.decode(file)) : file;
}

export async function ageUnarmor(text: string): Promise<Uint8Array> {
  const trimmed = text.trim();
  if (!trimmed.startsWith(ARMOR_BEGIN)) {
    // Bare Base64 is read too, that being an armoured block somebody has pasted the middle of.
    try {
      return fromBase64(trimmed);
    } catch {
      throw new Error(`That is not an armoured age file — one opens with ${ARMOR_BEGIN}`);
    }
  }
  return (await library()).armor.decode(trimmed);
}

// The one key this page still draws, behind the button on the Identities box: an X25519 identity, which is what
// `age-keygen` writes with no flag and what every implementation of age reads. It is the secret half itself, so the
// recipient it answers for is derived under the box like any other and nothing has to be kept anywhere to be shown.
export async function generateAgeIdentity(): Promise<string> {
  return (await library()).generateX25519Identity();
}

// The one import of it on this page, dynamic for the reason `pgp.ts`'s is: ML-KEM is the largest thing either family
// can reach, and a visit that picks AES-GCM should not pay for a key exchange it will never run.
function library(): Promise<typeof import("age-encryption")> {
  return import("age-encryption");
}

// typage answers a key it cannot read with "invalid recipient", which is true and is not a sentence, so the line
// it came from is what a box holding several of them says back — cut short by the one `shortened` cuts an
// identity short by, that message and this one being the same complaint about the same two kinds of key.
function readKey(add: () => void, noun: string, line: string): void {
  try {
    add();
  } catch {
    throw new Error(`That is not an age ${noun}: ${shortened(line)}`);
  }
}

const UNOPENED = "no identity matched any of the file's recipients";

const ARMOR_BEGIN = "-----BEGIN AGE ENCRYPTED FILE-----";

// The armour is Base64, dashes and newlines and nothing else, so the bytes of one are read back as what they are.
const ASCII = new TextDecoder("ascii");
